'use client'

import { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, BarChart3, Bug, CalendarDays, Home, Sprout } from 'lucide-react'

import { Button } from '@/components/ui/button'
import Link from '@/lib/react-compat/link'
import { useRouter, usePathname } from '@/lib/react-compat/navigation'

const QUICK_LINKS = [
  { href: '/recommendation', label: '作物推荐', icon: Sprout, desc: 'AI 智能选种' },
  { href: '/price-prediction', label: '价格预测', icon: BarChart3, desc: '市场走势分析' },
  { href: '/disease-detection', label: '病害识别', icon: Bug, desc: '图片上传诊断' },
  { href: '/calendar', label: '农事日历', icon: CalendarDays, desc: '节气与农时' },
]

export default function NotFoundPage() {
  const router = useRouter()
  const pathname = usePathname()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60)
    return () => clearTimeout(t)
  }, [])

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
      return
    }
    router.replace('/')
  }

  return (
    <section className="field-rows relative flex min-h-screen min-h-[100dvh] items-center border-b border-border bg-background py-16 sm:py-20">
      <div className="container relative z-10 w-full">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Record card */}
          <div
            className={`transition-all duration-700 ease-out lg:col-span-5 ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="mb-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              <span className="h-px w-8 bg-muted-foreground" aria-hidden />
              田园智士 · 页面记录
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <span className="tabular text-7xl font-bold leading-none tracking-tight text-foreground sm:text-8xl">
                404
              </span>
              <span className="mb-1.5 inline-flex items-center gap-1.5 rounded-sm border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs font-semibold text-destructive">
                <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                未找到
              </span>
            </div>

            <h1 className="mt-6 text-2xl font-bold text-foreground sm:text-3xl">
              这块田暂时没有找到
            </h1>
            <p className="mt-3 max-w-md text-muted-foreground">
              访问的页面可能已迁移、删除，或链接地址有误。请从右侧或下方入口重新出发。
            </p>

            {pathname && (
              <div className="mt-6 flex items-center gap-3 border-t border-dashed border-border pt-4 text-xs text-muted-foreground">
                <span className="shrink-0 font-semibold uppercase tracking-wider">请求路径</span>
                <code className="tabular truncate rounded-sm bg-muted px-2 py-1 text-foreground">
                  {pathname}
                </code>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Button asChild size="lg" className="rounded-sm bg-green-700 px-7 hover:bg-green-800">
                <Link href="/">
                  <Home className="mr-2 h-4 w-4" />
                  返回首页
                </Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleBack}
                className="rounded-sm border-foreground/20 px-7"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                返回上一页
              </Button>
            </div>
          </div>

          {/* Quick links, same numbered-index pattern as the homepage features list */}
          <div
            className={`transition-all delay-150 duration-700 ease-out lg:col-span-7 ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              你可能在找
            </div>
            <div className="border-t border-border">
              {QUICK_LINKS.map((link, index) => {
                const Icon = link.icon
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-b border-border py-5 transition-colors hover:bg-muted/40"
                  >
                    <span className="tabular w-10 shrink-0 text-2xl font-bold text-muted-foreground/25 transition-colors group-hover:text-green-700 dark:group-hover:text-green-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="flex items-center gap-2.5">
                      <Icon className="h-4 w-4 shrink-0 text-green-700 dark:text-green-400" />
                      <div>
                        <p className="font-semibold text-foreground">{link.label}</p>
                        <p className="text-xs text-muted-foreground">{link.desc}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 group-hover:text-green-700 dark:group-hover:text-green-400" />
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
